import { useEffect, useRef } from "react";
import { useLessonDispatch } from "@context/hooks";
import type { SectionContent } from "@/ts/interfaces/lessons/lessonInterface";
import { logger } from "@/utils/loggingUtils";
import { useLearnosity } from "@shared/learnosity";
import { useGoNoAccess } from "@hooks/useGoNoAccess";

/** Ordered list of valid lesson sections. */
export const LESSON_SECTIONS = [
  "introduction",
  "learn",
  "practise",
  "apply",
  "reflection",
  "summary"
] as const;

export type LessonSection = (typeof LESSON_SECTIONS)[number];

/** Backoff delays for transient network retries (1.5s, 3s, 6s). */
const RETRY_DELAYS = [1500, 3000, 6000];
const ONLINE_WAIT_CEILING_MS = 30_000;

const isTransientNetworkError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") return false;
  const msg =
    (error as { message?: string }).message ??
    (error as { stack?: string }).stack ??
    "";
  return (
    msg.includes("Failed to load") ||
    msg.includes("Failed to fetch") ||
    msg.includes("Load failed") ||
    msg.includes("Network Error") ||
    msg.includes("ERR_NETWORK") ||
    msg.includes("XHR error") ||
    msg.includes("Fetch error")
  );
};

/**
 * Resolve a cover image URL from Meilisearch.
 * Meilisearch stores RELATIVE static paths. Prepend the CDN base URL.
 * Handles backward compatibility with legacy full URLs (starting with "http").
 */
const resolveCoverImageUrl = (url?: string): string => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const cdnBase = (import.meta.env.VITE_CDN_BASE_URL || "").replace(/\/+$/, "");
  if (!cdnBase) return url;
  return `${cdnBase}/${url.replace(/^\/+/, "")}`;
};

/**
 * BLD2-458 V2: Resolve per-section audio paths from Meilisearch into
 * full CDN URLs. Mirrors `resolveCoverImageUrl` — MS stores relative
 * paths under `audio_paths`; Portal/Lambda persist them on Polly
 * generation success.
 *
 * Shape in: `{ learn: "lessons/audio/280/learn/Olivia.mp3" }`
 * Shape out: `{ learn: "https://portalcdn.rc.euka.edu.au/lessons/audio/280/learn/Olivia.mp3" }`
 */
const resolveAudioPaths = (
  paths?: Record<string, string> | null
): Record<string, string> => {
  if (!paths || typeof paths !== "object") return {};
  const cdnBase = (import.meta.env.VITE_CDN_BASE_URL || "").replace(/\/+$/, "");
  return Object.fromEntries(
    Object.entries(paths)
      .filter(([, v]) => typeof v === "string" && v.length > 0)
      .map(([k, v]) => {
        if (v.startsWith("http")) return [k, v];
        const rel = v.replace(/^\/+/, "");
        return [k, cdnBase ? `${cdnBase}/${rel}` : rel];
      })
  );
};

interface SectionContentParams {
  currentSection: string;
  grade?: number;
  termId?: string;
  weekId?: string;
  lessonId?: number;
  subjectSlug?: string;
}

/**
 * Loads lesson section content on demand and initializes Learnosity.
 *
 * Handles:
 *  - Transient network retry with exponential backoff
 *  - Offline detection (waits for `online` event)
 *  - 403 → redirect to no-access page
 *  - Learnosity init deferred by one animation frame (PLAN-309) — gives
 *    the child page component's DOM containers time to commit before
 *    the SDK searches for `<span class="learnosity-item">` elements.
 *
 * See: PLAN-309
 */
const useLessonSectionContent = (params: SectionContentParams): void => {
  const { currentSection, grade, termId, weekId, lessonId, subjectSlug } =
    params;

  const { initialize, reset, setLearnosityError } = useLearnosity();
  const { fetchSectionContent } = useLessonDispatch();
  const navigateToNoAccess = useGoNoAccess();

  // PLAN-310: hold the most recent VALID section across renders.
  //
  // The URL briefly flips between /lesson/{id} (currentSection = lessonId,
  // INVALID) and /lesson/{id}/introduction (VALID) while useLessonEntryGuard
  // resolves which step to land on. If we keyed the effect directly off
  // `currentSection`, that brief invalid flip would change the effect
  // dependency, fire the cleanup, set isCancelled = true on the in-flight
  // 'introduction' fetch, and leave the page stuck.
  //
  // By snapshotting the last valid value in a ref, transient invalid sections
  // become a no-op for the effect (deps don't change → no cleanup → fetch
  // continues to completion). The ref is updated DURING RENDER (before the
  // effect's dep-comparison), so the dep value the effect sees is always the
  // stable, valid section.
  const lastValidSectionRef = useRef<string>("");
  const isValidSection = (LESSON_SECTIONS as readonly string[]).includes(
    currentSection
  );
  if (isValidSection && currentSection !== lastValidSectionRef.current) {
    lastValidSectionRef.current = currentSection;
  }
  const effectiveSection = lastValidSectionRef.current;

  // Surface visibility into the suppression for debugging — log only on the
  // transition into an invalid section, not on every render.
  const lastLoggedInvalidRef = useRef<string>("");
  if (
    !isValidSection &&
    currentSection &&
    currentSection !== lastLoggedInvalidRef.current
  ) {
    lastLoggedInvalidRef.current = currentSection;
    logger.log(
      "[useLessonSectionContent] ignoring transient invalid section (in-flight fetch preserved)",
      { currentSection, effectiveSection }
    );
  }

  useEffect(() => {
    if (!grade || !termId || !weekId || !lessonId || !subjectSlug) {
      logger.log("[Fetch Section Effect] Missing data", {
        grade,
        termId,
        weekId,
        lessonId,
        subjectSlug
      });
      return;
    }

    // Wait until a valid section has been observed at least once.
    // On a brief invalid flip, `effectiveSection` keeps its previous valid
    // value (or "" if no valid section has been seen yet) and this effect
    // does NOT re-run — preserving any in-flight fetch.
    if (!effectiveSection) {
      logger.log("[Fetch Section Effect] No valid section yet", {
        currentSection
      });
      return;
    }

    let isCancelled = false;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let rafId: ReturnType<typeof requestAnimationFrame> | null = null;
    let onlineHandler: (() => void) | null = null;

    const initFromContent = (sc: SectionContent) => {
      // PLAN-309: Defer Learnosity init by one animation frame so the
      // child page component's <span class="learnosity-item"> containers
      // have time to commit to the DOM before the SDK looks for them.
      rafId = requestAnimationFrame(() => {
        if (isCancelled) return;
        initialize({
          learnosityItems: sc.activity_request.request.request.items,
          activityRequest: sc.activity_request.request,
          lessonData: {
            curriculum: sc.curriculum_connection,
            learningObjectives: sc.learning_objectives,
            materials: sc.list_of_materials,
            termBasedMessaging: sc.term_based_messages,
            status: sc.status,
            coverImageUrl: resolveCoverImageUrl(sc.cover_image_url),
            coverImageAltText: sc.cover_image_alt_text || "",
            // BLD2-458: per-grade Read Aloud rollout flag from MS.
            audioEnabled: Boolean(sc.audio_enabled),
            // BLD2-458 V2: per-section audio URLs (resolved from
            // relative MS paths to full CDN URLs). Empty when audio
            // isn't ready yet — AudioPlayer hides on empty.
            audioPaths: resolveAudioPaths(sc.audio_paths),
            // BLD2-463: per-student notification banner visibility from
            // user.profile.meta (BLD2-462). Default true — treat absent
            // field (pre-BLD2-462 sessions) as "show banner".
            audioBannerVisible: sc.audio_banner_visible !== false,
            applyActivityItems: sc.apply_activity_item_uuids,
            reflectionActivityItems: sc.reflection_activity_item_uuids,
            textbookActivityItems: sc.textbook_activity_item_uuids,
            learnActivityItems: sc.learn_activity_item_uuids
          }
        });
      });
    };

    logger.log("[Fetch Section Effect] section load start", {
      currentSection: effectiveSection,
      grade,
      termId,
      weekId,
      lessonId,
      subjectSlug
    });

    const handleSuccess = (response: unknown) => {
      if (isCancelled) {
        logger.warn(
          "[Fetch Section Effect] section load success ignored (cancelled)"
        );
        return;
      }

      logger.log(
        "%c[Fetch Section Effect] section load success",
        "color: #B6DE83",
        { section: effectiveSection }
      );

      const sc = response as SectionContent;
      initFromContent(sc);
    };

    const fetchParams = {
      section: effectiveSection,
      termId: Number(termId),
      weekId: Number(weekId),
      gradeId: grade,
      lessonId,
      subjectSlug
    };

    const waitForOnline = (): Promise<void> => {
      if (typeof navigator === "undefined" || navigator.onLine) {
        return Promise.resolve();
      }
      return new Promise<void>(resolve => {
        const timer = setTimeout(resolve, ONLINE_WAIT_CEILING_MS);
        onlineHandler = () => {
          clearTimeout(timer);
          resolve();
        };
        window.addEventListener("online", onlineHandler, { once: true });
      });
    };

    const fetchWithRetry = (attempt: number): void => {
      if (isCancelled) return;

      fetchSectionContent(fetchParams)
        .then(handleSuccess)
        .catch(error => {
          if (
            error?.name === "AbortError" ||
            error?.message?.includes("abort")
          ) {
            logger.log(
              "[Fetch Section Effect] section content fetch aborted/cancelled"
            );
            return;
          }
          if (isCancelled) return;

          if (error?.code === 403) {
            navigateToNoAccess();
            return;
          }

          if (
            isTransientNetworkError(error) &&
            attempt < RETRY_DELAYS.length
          ) {
            const delay = RETRY_DELAYS[attempt];
            logger.warn(
              `[Fetch Section Effect] transient network error, retry ${attempt + 1}/${RETRY_DELAYS.length} in ${delay}ms`,
              { section: effectiveSection }
            );

            waitForOnline().then(() => {
              if (isCancelled) return;
              retryTimeoutId = setTimeout(
                () => fetchWithRetry(attempt + 1),
                delay
              );
            });
            return;
          }

          logger.warn(
            "[Fetch Section Effect] error fetching section content — showing error page",
            {
              section: effectiveSection,
              grade,
              termId,
              weekId,
              lessonId,
              attempt
            },
            error
          );
          setLearnosityError("We couldn't load the lesson. Please try again.");
        });
    };

    fetchWithRetry(0);

    return () => {
      isCancelled = true;
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
      if (rafId) cancelAnimationFrame(rafId);
      if (onlineHandler) {
        window.removeEventListener("online", onlineHandler);
      }
      reset();
    };
  }, [effectiveSection, grade, termId, weekId, lessonId, subjectSlug]);
};

export default useLessonSectionContent;
